// Phase 6 — Track D T-D5. /dates route.
// Phase 7 D6 — Track E T-E3. Adds a DatesToolbar at the top, wires
// the row click → DateDialog (edit), the toolbar Add button →
// DateDialog (add), and the destructive-toast surface for delete
// confirmations.
//
// Read-only list ordered by `next_occurrence_on` ascending (the API
// preserves this ordering; the SPA renders as-received). Empty-state copy
// is rephrased neutrally inline rather than re-using the shared bundle's
// "Add a date in Phase 7" string — that copy leaks dev framing into a
// user-facing surface (per blueprint section 13.4).

import * as React from 'react';
import { copy } from '@dailydeck/shared/copy';
import type { DateEvent } from '@dailydeck/shared/types';
import { useDates } from '../../hooks/useDates.js';
import { DateRow } from './DateRow.js';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { SkeletonRow } from '../../components/ui/Skeleton.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { DatesToolbar } from '../../components/DatesToolbar.js';
import type { DateDialogMode } from './DateDialog.js';
import {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastClose,
} from '../../components/ui/Toast.js';

// Lazy-load the write-surface dialogs. Each one imports react-hook-form +
// zod resolver + several Radix primitives; loading them on demand keeps
// the /dates first paint light and reduces the heap footprint of the
// route's component tree under jsdom (matches Phase 7 D4's pattern in
// Payments.tsx).
const DateDialog = React.lazy(() =>
  import('./DateDialog.js').then((m) => ({ default: m.DateDialog })),
);
const DeleteDateDialog = React.lazy(() =>
  import('./DeleteDateDialog.js').then((m) => ({ default: m.DeleteDateDialog })),
);

interface ToastState {
  id: number;
  title: string;
  variant: 'default' | 'danger';
}

export function Dates(): JSX.Element {
  React.useEffect(() => {
    document.title = `DailyDeck — ${copy.dates.pageTitle}`;
  }, []);

  const query = useDates();
  const today = React.useMemo(() => new Date(), []);

  // Dialog state
  const [dateDialog, setDateDialog] = React.useState<{
    open: boolean;
    mode: DateDialogMode;
  }>({ open: false, mode: { kind: 'add' } });
  const [deleteTarget, setDeleteTarget] = React.useState<DateEvent | null>(null);
  const [toast, setToast] = React.useState<ToastState | null>(null);

  const handleAdd = React.useCallback(() => {
    setDateDialog({ open: true, mode: { kind: 'add' } });
  }, []);

  const handleRowClick = React.useCallback((event: DateEvent) => {
    setDateDialog({ open: true, mode: { kind: 'edit', event } });
  }, []);

  const handleDateDialogChange = React.useCallback((next: boolean) => {
    setDateDialog((prev) => ({ ...prev, open: next }));
  }, []);

  const handleRequestDelete = React.useCallback((event: DateEvent) => {
    // Close the edit sheet first; deletion uses a Dialog (single modal stack).
    setDateDialog((prev) => ({ ...prev, open: false }));
    setDeleteTarget(event);
  }, []);

  const handleDeleteOpenChange = React.useCallback((next: boolean) => {
    if (!next) setDeleteTarget(null);
  }, []);

  const handleDeleted = React.useCallback(() => {
    setToast({
      id: Date.now(),
      title: copy.dates.deletedToast,
      variant: 'danger',
    });
  }, []);

  return (
    <div className="space-y-6" data-testid="dates-route">
      <header className="flex flex-col gap-4">
        <div>
          <h1 className="text-title-1 font-semibold text-text-primary">{copy.dates.pageTitle}</h1>
          <p className="text-body text-text-secondary mt-1">{copy.dates.pageSubtitle}</p>
        </div>
        <DatesToolbar onAdd={handleAdd} />
      </header>

      {query.isPending && (
        <div className="rounded-lg border border-border-subtle bg-surface-raised" aria-label={copy.dates.loadingLabel}>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {query.isError && (
        <Card data-testid="dates-error">
          <CardHeader>
            <CardTitle>{copy.dates.errorTitle}</CardTitle>
            <CardDescription>{copy.errors.network_unavailable}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-body text-text-secondary">{copy.dates.errorBody}</p>
          </CardContent>
          <CardFooter>
            <Button variant="secondary" onClick={() => void query.refetch()}>
              {copy.dates.retry}
            </Button>
          </CardFooter>
        </Card>
      )}

      {query.isSuccess && query.data.length === 0 && (
        <EmptyState
          title={copy.dates.emptyTitle}
          description="No upcoming dates yet."
        />
      )}

      {query.isSuccess && query.data.length > 0 && (
        <div className="rounded-lg border border-border-subtle bg-surface-raised overflow-hidden" data-testid="dates-list">
          {query.data.map((event) => (
            <div
              key={event.id}
              role="button"
              tabIndex={0}
              onClick={() => handleRowClick(event)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleRowClick(event);
                }
              }}
              className="cursor-pointer"
              data-testid="date-row-trigger"
            >
              <DateRow event={event} today={today} />
            </div>
          ))}
        </div>
      )}

      <React.Suspense fallback={null}>
        {dateDialog.open && (
          <DateDialog
            open={dateDialog.open}
            onOpenChange={handleDateDialogChange}
            mode={dateDialog.mode}
            onRequestDelete={handleRequestDelete}
          />
        )}
        {deleteTarget !== null && (
          <DeleteDateDialog
            open={deleteTarget !== null}
            onOpenChange={handleDeleteOpenChange}
            event={deleteTarget}
            onDeleted={handleDeleted}
          />
        )}
      </React.Suspense>

      {toast !== null && (
        <ToastProvider duration={5000}>
          <Toast
            key={toast.id}
            open
            onOpenChange={(o) => {
              if (!o) setToast(null);
            }}
            variant={toast.variant}
            data-testid="dates-toast"
          >
            <div className="flex flex-col gap-1">
              <ToastTitle>{toast.title}</ToastTitle>
            </div>
            <ToastClose />
          </Toast>
          <ToastViewport />
        </ToastProvider>
      )}
    </div>
  );
}
